/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useLayoutEffect, useRef, useImperativeHandle, useCallback } from 'react';
import { resolveStatementAttachmentHref } from '../utils/statementAttachments';
import { offsetsToRange, rangeRectsRelativeTo, rangeToOffsets } from '../utils/domTextOffsets';
import { runPython, deliverInteractiveStdinLine, deliverCanvasEvent } from './PyodideRunner';
import Modal from './Modal';

function resetCanvasSessionUi() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('diobuch-canvas-control', { detail: { type: 'stop_tick' } }));
  window.dispatchEvent(new CustomEvent('diobuch-canvas', { detail: { type: 'session', active: false } }));
}

const RUNNER_KEY = 'runner';

/** toggle() ничего не возвращает — нельзя писать `toggle?.() ?? run?.()`: оба вызова сработают. */
export function invokeRunRequest(runRef) {
  const api = runRef?.current;
  if (typeof api?.toggle === 'function') {
    api.toggle();
  } else {
    api?.run?.();
  }
}

/** Плоское имя файла для виртуальной ФС Pyodide (без путей). */
export function sanitizeRunnerFileKey(raw) {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const base = trimmed.replace(/\\/g, '/').split('/').filter(Boolean).pop() ?? '';
  if (!base || base === '.' || base === '..' || base.includes('..')) return null;
  return base;
}

function uniqueRunnerFileName(desired, existingKeys) {
  const set = new Set(existingKeys);
  if (!set.has(desired)) return desired;
  const dot = desired.lastIndexOf('.');
  const stem = dot > 0 ? desired.slice(0, dot) : desired;
  const ext = dot > 0 ? desired.slice(dot) : '';
  let i = 2;
  let candidate;
  do {
    candidate = `${stem}_${i}${ext}`;
    i += 1;
  } while (set.has(candidate));
  return candidate;
}

/** Текстовые вложения для виртуальной ФС Python. .xlsx/.ods/.xls — только ссылки в условии. */
const RUNNER_TEXT_SUFFIXES = new Set(['.txt', '.csv', '.tsv', '.dat', '.in', '.out', '.json']);

function attachmentFileSuffix(name) {
  const base = typeof name === 'string' ? name : '';
  const dot = base.lastIndexOf('.');
  return dot >= 0 ? base.slice(dot).toLowerCase() : '';
}

function isRunnerTextAttachment(name) {
  return RUNNER_TEXT_SUFFIXES.has(attachmentFileSuffix(name));
}

/** Очередь URL: в БД часто /media/ege17/…, файлы на диске — media/17data/… */
function attachmentMediaFetchUrls(attUrl) {
  const primary = resolveStatementAttachmentHref(attUrl);
  const list = [primary];
  const raw = typeof attUrl === 'string' ? attUrl.trim() : '';
  const isEge17 = /(?:^|\/)(?:ege17|17data)(?:\/|$)/i.test(raw);
  if (!isEge17) return list;
  const seg = raw.split('/').filter(Boolean);
  const tail = seg.length ? seg[seg.length - 1] : '';
  const base = sanitizeRunnerFileKey(tail);
  if (base) {
    const alt = `/media/17data/${base}`;
    if (!list.includes(alt)) list.push(alt);
  }
  return list;
}

/**
 * Загружает текстовые вложения задачи в виртуальные файлы Python.
 * Таблицы (.xlsx/.ods/.xls) пропускаем: качать их как текст бессмысленно и тяжело для сети.
 * Частичный успех: ошибки по отдельным элементам в loadErrors.
 * @param {Array<{ name?: string, url?: string }>} attachments
 * @returns {Promise<{ files: Record<string, string>, loadErrors: Array<{ name: string, message: string }> }>}
 */
export async function fetchStatementAttachmentsAsFiles(attachments) {
  if (!Array.isArray(attachments) || attachments.length === 0) {
    return { files: {}, loadErrors: [] };
  }
  const files = {};
  const loadErrors = [];
  let skippedBinary = 0;
  for (const att of attachments) {
    const rawName = att?.name;
    const safe = sanitizeRunnerFileKey(typeof rawName === 'string' ? rawName : '');
    const urlStr = typeof att?.url === 'string' ? att.url.trim() : '';
    const hasUrl = Boolean(urlStr);

    if (!safe && !hasUrl) {
      loadErrors.push({
        name: '—',
        message: 'Вложение без имени файла и без URL.',
      });
      continue;
    }
    if (!safe) {
      const fromUrl = sanitizeRunnerFileKey(urlStr.split('/').pop() || '') || 'файл';
      loadErrors.push({
        name: fromUrl,
        message: 'Не указано имя файла во вложении.',
      });
      continue;
    }
    if (!isRunnerTextAttachment(safe)) {
      skippedBinary += 1;
      continue;
    }
    if (!hasUrl) {
      loadErrors.push({
        name: safe,
        message: 'Не указан URL файла.',
      });
      continue;
    }

    const key = uniqueRunnerFileName(safe, Object.keys(files));
    try {
      const candidates = attachmentMediaFetchUrls(att.url);
      let lastStatus = 0;
      let loadedText = null;
      for (const abs of candidates) {
        if (!abs) continue;
        const res = await fetch(abs, { credentials: 'include' });
        lastStatus = res.status;
        if (res.ok) {
          loadedText = await res.text();
          break;
        }
      }
      if (loadedText == null) {
        loadErrors.push({
          name: key,
          message: `Не удалось загрузить «${key}» (HTTP ${lastStatus || '?'}). Проверьте путь и раздачу /media на сервере.`,
        });
      } else {
        files[key] = loadedText;
      }
    } catch (e) {
      const msg = e?.message || 'неизвестная ошибка';
      loadErrors.push({
        name: key,
        message: `Ошибка при загрузке «${key}»: ${msg}`,
      });
    }
  }
  if (skippedBinary > 0 && Object.keys(files).length === 0 && loadErrors.length === 0) {
    loadErrors.push({
      name: 'файл .txt',
      message: 'Нет текстового файла для программы. Таблицы .xlsx/.ods скачиваются ссылкой в условии; в коде нужен одноимённый .txt.',
    });
  }
  return { files, loadErrors };
}

const RunnerContext = createContext(null);

export function useRunnerContext() {
  const ctx = useContext(RunnerContext);
  if (!ctx) throw new Error('useRunnerContext must be used within CollaborativeRunnerProvider');
  return ctx;
}

const MAX_OUTPUT_LINES = 200;
const MAX_OUTPUT_CHARS = 64 * 1024;
const YJS_OUTPUT_THROTTLE_MS = 180;

function trimOutputLines(text) {
  if (!text) return '';
  let next = text;
  const lines = next.split('\n');
  if (lines.length > MAX_OUTPUT_LINES) {
    next = lines.slice(-MAX_OUTPUT_LINES).join('\n');
  }
  if (next.length > MAX_OUTPUT_CHARS) {
    next = next.slice(-MAX_OUTPUT_CHARS);
  }
  return next;
}

function makeRunnerRequestId(prefix) {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

const AUTO_CLEAR_STORAGE_KEY = 'workAutoClearOutput';
const INTERACTIVE_STDIN_STORAGE_KEY = 'workInteractiveStdin';

function getStoredAutoClear() {
  try {
    return localStorage.getItem(AUTO_CLEAR_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function getStoredInteractiveStdin() {
  try {
    return localStorage.getItem(INTERACTIVE_STDIN_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function makeInputRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `stdin-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

const SCENE_PUBLISH_MS = 80;
const AWARENESS_SCENE_KEY = 'canvasScene';

function emptySceneState() {
  return {
    objects: new Map(),
    cameraX: 0,
    cameraY: 0,
    session: false,
  };
}

function cloneSceneObject(cmd, existing = null) {
  const base = existing ? { ...existing } : {};
  const id = String(cmd.id || base.id || '');
  return {
    id,
    kind: cmd.kind != null ? String(cmd.kind) : (base.kind || 'circle'),
    x: cmd.x != null ? Number(cmd.x) || 0 : (base.x || 0),
    y: cmd.y != null ? Number(cmd.y) || 0 : (base.y || 0),
    r: cmd.r != null ? Number(cmd.r) || 10 : (base.r ?? 10),
    w: cmd.w != null ? Number(cmd.w) || 20 : (base.w ?? 20),
    h: cmd.h != null ? Number(cmd.h) || 20 : (base.h ?? 20),
    fill: cmd.fill != null ? String(cmd.fill) : (base.fill ?? '#38bdf8'),
    text: cmd.text != null ? String(cmd.text) : (base.text ?? ''),
    size: cmd.size != null ? Number(cmd.size) || 16 : (base.size ?? 16),
    image: cmd.image != null ? String(cmd.image) : (base.image ?? 'hero_idle'),
    flip: cmd.flip != null ? !!cmd.flip : !!base.flip,
  };
}

function applyPeerSceneSnapshot(scene) {
  if (typeof window === 'undefined' || !scene || typeof scene !== 'object') return;
  const active = !!scene.active;
  // Пир не крутит тики — только рисует и шлёт ввод.
  window.dispatchEvent(new CustomEvent('diobuch-canvas-control', { detail: { type: 'stop_tick' } }));
  window.dispatchEvent(new CustomEvent('diobuch-canvas', {
    detail: {
      type: 'scene_snapshot',
      active,
      cameraX: Number(scene.cameraX) || 0,
      cameraY: Number(scene.cameraY) || 0,
      objects: Array.isArray(scene.objects) ? scene.objects : [],
      peer: active,
    },
  }));
  if (!active) {
    window.dispatchEvent(new CustomEvent('diobuch-canvas', { detail: { type: 'session', active: false } }));
  }
}

export function CollaborativeRunnerProvider({ code, ydoc, awareness = null, runRef, canvasConfig = null, children }) {
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [stdin, setStdin] = useState('');
  const [files, setFiles] = useState({}); // { "имя.txt": "содержимое", ... }
  const [attachmentLoadErrors, setAttachmentLoadErrors] = useState(
    /** @type {Array<{ name: string, message: string }>} */ ([]),
  );
  const [outputSelection, setOutputSelection] = useState({ start: 0, end: 0 });
  const [lastRunStartedAt, setLastRunStartedAt] = useState(null);
  const [lastRunFinishedAt, setLastRunFinishedAt] = useState(null);
  const [lastRunDurationMs, setLastRunDurationMs] = useState(null);
  const [autoClearOutput, setAutoClearOutputState] = useState(getStoredAutoClear);
  const [interactiveStdin, setInteractiveStdinState] = useState(getStoredInteractiveStdin);
  const [pendingInputRequestId, setPendingInputRequestId] = useState(null);
  const [stdinDraft, setStdinDraftState] = useState('');
  const [inputModalOpen, setInputModalOpenState] = useState(false);
  const [codeAtLastRun, setCodeAtLastRun] = useState(null);
  const fileInputRef = useRef(null);
  const outputRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isLocalRunRef = useRef(false);
  const stdinRequestIdRef = useRef(null);
  const applyingRemoteDraftRef = useRef(false);
  const canvasConfigRef = useRef(canvasConfig);
  canvasConfigRef.current = canvasConfig && typeof canvasConfig === 'object' ? canvasConfig : {};
  const outputSyncTimerRef = useRef(null);
  const pendingYjsOutputRef = useRef(null);
  const lastStopRequestIdRef = useRef(null);
  const lastReconnectRequestIdRef = useRef(null);
  const publishedStopRequestIdRef = useRef(null);
  const onPeerReconnectRef = useRef(null);
  const runnerSignalsReadyRef = useRef(false);
  const [peerLoading, setPeerLoading] = useState(false);
  const awarenessRef = useRef(awareness);
  awarenessRef.current = awareness;
  const sceneStateRef = useRef(emptySceneState());
  const scenePublishTimerRef = useRef(null);
  /** @type {React.MutableRefObject<Map<string, Set<string>>>} */
  const keysByClientRef = useRef(new Map());
  const lastCanvasInputSeqRef = useRef(null);
  const lastPeerSceneTsRef = useRef(0);

  const flushScenePublish = useCallback(() => {
    const aw = awarenessRef.current;
    if (!aw || !isLocalRunRef.current) return;
    const s = sceneStateRef.current;
    const objects = [];
    for (const obj of s.objects.values()) {
      objects.push({ ...obj });
    }
    try {
      aw.setLocalStateField(AWARENESS_SCENE_KEY, {
        active: !!s.session,
        cameraX: s.cameraX,
        cameraY: s.cameraY,
        objects,
        ts: Date.now(),
      });
    } catch {
      // awareness уже уничтожен
    }
  }, []);

  const scheduleScenePublish = useCallback(() => {
    if (!isLocalRunRef.current || !awarenessRef.current) return;
    if (scenePublishTimerRef.current != null) return;
    scenePublishTimerRef.current = setTimeout(() => {
      scenePublishTimerRef.current = null;
      flushScenePublish();
    }, SCENE_PUBLISH_MS);
  }, [flushScenePublish]);

  const clearAndPublishScene = useCallback(() => {
    if (scenePublishTimerRef.current != null) {
      clearTimeout(scenePublishTimerRef.current);
      scenePublishTimerRef.current = null;
    }
    sceneStateRef.current = emptySceneState();
    keysByClientRef.current.clear();
    const aw = awarenessRef.current;
    if (aw) {
      try {
        aw.setLocalStateField(AWARENESS_SCENE_KEY, {
          active: false,
          cameraX: 0,
          cameraY: 0,
          objects: [],
          ts: Date.now(),
        });
      } catch {
        // ignore
      }
    }
  }, []);

  const trackSceneFromCmd = useCallback((cmd) => {
    if (!cmd || typeof cmd !== 'object') return;
    const type = String(cmd.type || '').toLowerCase();
    const s = sceneStateRef.current;
    if (type === 'session') {
      s.session = !!cmd.active;
      if (!cmd.active) {
        // сессия закрыта — оставляем объекты до clear, но active=false
      }
      return;
    }
    if (type === 'camera') {
      s.cameraX = Number(cmd.x) || 0;
      s.cameraY = Number(cmd.y) || 0;
      return;
    }
    if (type === 'scene_clear') {
      s.objects.clear();
      s.cameraX = 0;
      s.cameraY = 0;
      return;
    }
    if (type === 'scene_spawn') {
      const id = String(cmd.id || '');
      if (!id) return;
      s.objects.set(id, cloneSceneObject(cmd));
      return;
    }
    if (type === 'scene_update') {
      const id = String(cmd.id || '');
      const prev = s.objects.get(id);
      if (!prev) return;
      s.objects.set(id, cloneSceneObject(cmd, prev));
      return;
    }
    if (type === 'scene_destroy') {
      s.objects.delete(String(cmd.id || ''));
    }
  }, []);

  const unionHeldKeys = useCallback(() => {
    const union = new Set();
    for (const set of keysByClientRef.current.values()) {
      for (const k of set) union.add(k);
    }
    return union;
  }, []);

  const applyMergedKeyEvent = useCallback((clientId, detail) => {
    const code = String(detail?.code || '').trim() || String(detail?.key || '').trim();
    if (!code) return;
    const cid = String(clientId || 'local');
    if (!keysByClientRef.current.has(cid)) {
      keysByClientRef.current.set(cid, new Set());
    }
    const clientKeys = keysByClientRef.current.get(cid);
    const before = unionHeldKeys();
    const t = String(detail?.type || '');
    if (t === 'keydown') {
      clientKeys.add(code);
    } else if (t === 'keyup') {
      clientKeys.delete(code);
    } else {
      return;
    }
    const after = unionHeldKeys();
    for (const k of after) {
      if (!before.has(k)) {
        deliverCanvasEvent({ type: 'keydown', code: k, key: detail.key || '' });
      }
    }
    for (const k of before) {
      if (!after.has(k)) {
        deliverCanvasEvent({ type: 'keyup', code: k, key: detail.key || '' });
      }
    }
  }, [unionHeldKeys]);

  const publishCanvasInput = useCallback((detail) => {
    if (!ydoc || !detail) return;
    const aw = awarenessRef.current;
    const clientId = aw ? String(aw.clientID) : 'peer';
    const seq = makeRunnerRequestId('cvin');
    try {
      const m = ydoc.getMap(RUNNER_KEY);
      m.set('canvasInputEvent', {
        type: detail.type,
        x: detail.x,
        y: detail.y,
        code: detail.code || '',
        key: detail.key || '',
        clientId,
      });
      m.set('canvasInputSeq', seq);
    } catch {
      // ignore
    }
  }, [ydoc]);

  const handleHostCanvasInput = useCallback((detail) => {
    if (!detail || typeof detail !== 'object') return;
    const t = String(detail.type || '');
    if (t === 'click') {
      deliverCanvasEvent(detail);
      return;
    }
    if (t === 'keydown' || t === 'keyup') {
      const aw = awarenessRef.current;
      const clientId = aw ? String(aw.clientID) : 'local';
      applyMergedKeyEvent(clientId, detail);
      return;
    }
    deliverCanvasEvent(detail);
  }, [applyMergedKeyEvent]);

  const applyMergedKeyEventRef = useRef(applyMergedKeyEvent);
  applyMergedKeyEventRef.current = applyMergedKeyEvent;

  useEffect(() => {
    function onCanvasInput(ev) {
      const detail = ev?.detail || { type: 'stop' };
      const t = String(detail.type || '');

      if (t === 'tick' || t === 'after') {
        if (isLocalRunRef.current) {
          deliverCanvasEvent(detail);
        }
        return;
      }

      if (t === 'click' || t === 'keydown' || t === 'keyup') {
        if (isLocalRunRef.current) {
          handleHostCanvasInput(detail);
        } else if (ydoc) {
          // Пир: ввод уходит к хосту через Y.Map
          publishCanvasInput(detail);
        } else {
          deliverCanvasEvent(detail);
        }
        return;
      }

      if (isLocalRunRef.current) {
        deliverCanvasEvent(detail);
      }
    }
    window.addEventListener('diobuch-canvas-input', onCanvasInput);
    return () => window.removeEventListener('diobuch-canvas-input', onCanvasInput);
  }, [ydoc, handleHostCanvasInput, publishCanvasInput]);

  // Зеркало сцены с хоста через awareness (не пишется в yjs_state).
  useEffect(() => {
    if (!awareness) return undefined;
    lastPeerSceneTsRef.current = 0;
    const onChange = () => {
      if (isLocalRunRef.current) return;
      let best = null;
      awareness.getStates().forEach((state, clientId) => {
        if (clientId === awareness.clientID) return;
        const scene = state?.[AWARENESS_SCENE_KEY];
        if (!scene || typeof scene !== 'object') return;
        if (!best || (Number(scene.ts) || 0) > (Number(best.ts) || 0)) {
          best = scene;
        }
      });
      if (!best) {
        // Хост ушёл из комнаты — гасим зеркало.
        if (lastPeerSceneTsRef.current) {
          lastPeerSceneTsRef.current = 0;
          applyPeerSceneSnapshot({
            active: false,
            cameraX: 0,
            cameraY: 0,
            objects: [],
            ts: Date.now(),
          });
        }
        return;
      }
      const ts = Number(best.ts) || 0;
      // Awareness часто меняется из‑за курсоров — не перерисовываем без нового снимка.
      if (ts && ts === lastPeerSceneTsRef.current) return;
      lastPeerSceneTsRef.current = ts;
      applyPeerSceneSnapshot(best);
    };
    awareness.on('change', onChange);
    onChange();
    return () => {
      awareness.off('change', onChange);
    };
  }, [awareness]);

  const setAutoClearOutput = (v) => {
    setAutoClearOutputState(v);
    try {
      localStorage.setItem(AUTO_CLEAR_STORAGE_KEY, String(!!v));
    } catch {
      // ignore
    }
  };

  const setInteractiveStdin = useCallback((v) => {
    const next = !!v;
    setInteractiveStdinState(next);
    try {
      localStorage.setItem(INTERACTIVE_STDIN_STORAGE_KEY, String(next));
    } catch {
      // ignore
    }
    if (ydoc) {
      const m = ydoc.getMap(RUNNER_KEY);
      m.set('interactiveStdin', next);
    }
  }, [ydoc]);

  const setStdinDraft = useCallback((val) => {
    const next = String(val ?? '');
    setStdinDraftState(next);
    if (ydoc && !applyingRemoteDraftRef.current) {
      const m = ydoc.getMap(RUNNER_KEY);
      m.set('inputDraft', next);
    }
  }, [ydoc]);

  const setInputModalOpen = useCallback((open) => {
    const next = !!open;
    setInputModalOpenState(next);
    if (ydoc) {
      const m = ydoc.getMap(RUNNER_KEY);
      m.set('inputModalOpen', next);
    }
  }, [ydoc]);

  const clearPendingInputState = useCallback(() => {
    stdinRequestIdRef.current = null;
    setPendingInputRequestId(null);
    setStdinDraftState('');
    if (ydoc) {
      const m = ydoc.getMap(RUNNER_KEY);
      m.delete('inputRequestId');
      m.delete('inputResponseId');
      m.delete('inputResponseLine');
      m.set('inputDraft', '');
    }
  }, [ydoc]);

  const flushOutputToYjs = useCallback((text, errorText) => {
    if (!ydoc) return;
    try {
      const m = ydoc.getMap(RUNNER_KEY);
      if (text !== undefined) m.set('output', trimOutputLines(text ?? ''));
      if (errorText !== undefined) m.set('error', errorText ?? '');
    } catch {
      // ydoc уже уничтожен
    }
  }, [ydoc]);

  const scheduleOutputToYjs = useCallback((text) => {
    if (!ydoc) return;
    pendingYjsOutputRef.current = text;
    if (outputSyncTimerRef.current != null) return;
    outputSyncTimerRef.current = setTimeout(() => {
      outputSyncTimerRef.current = null;
      const pending = pendingYjsOutputRef.current;
      pendingYjsOutputRef.current = null;
      if (pending != null) flushOutputToYjs(pending, '');
    }, YJS_OUTPUT_THROTTLE_MS);
  }, [ydoc, flushOutputToYjs]);

  const cancelScheduledOutputSync = useCallback(() => {
    if (outputSyncTimerRef.current != null) {
      clearTimeout(outputSyncTimerRef.current);
      outputSyncTimerRef.current = null;
    }
    pendingYjsOutputRef.current = null;
  }, []);

  const appendOutputText = useCallback((chunk) => {
    if (typeof chunk !== 'string' || !chunk) return;
    setOutput((prev) => {
      const next = trimOutputLines((prev || '') + chunk);
      scheduleOutputToYjs(next);
      return next;
    });
  }, [scheduleOutputToYjs]);

  const clearOutput = () => {
    cancelScheduledOutputSync();
    setOutput('');
    setError('');
    flushOutputToYjs('', '');
  };

  const requestInteractiveStdin = useCallback(({ requestId }) => {
    const reqId = requestId != null && requestId !== '' ? String(requestId) : makeInputRequestId();
    stdinRequestIdRef.current = reqId;
    setPendingInputRequestId(reqId);
    setStdinDraftState('');
    if (ydoc) {
      const m = ydoc.getMap(RUNNER_KEY);
      m.set('inputRequestId', reqId);
      m.set('inputDraft', '');
      m.delete('inputResponseId');
      m.delete('inputResponseLine');
    }
  }, [ydoc]);

  const submitInteractiveStdin = useCallback((line) => {
    const reqId = stdinRequestIdRef.current || pendingInputRequestId;
    if (!reqId) return;
    const text = String(line ?? '');
    // Echo в вывод пишет Python (input = <value>) через stdout — не дублируем здесь.

    if (isLocalRunRef.current) {
      deliverInteractiveStdinLine(text);
    } else if (ydoc) {
      const m = ydoc.getMap(RUNNER_KEY);
      m.set('inputResponseId', reqId);
      m.set('inputResponseLine', text);
    }

    if (ydoc) {
      const m = ydoc.getMap(RUNNER_KEY);
      m.delete('inputRequestId');
      m.set('inputDraft', '');
    }
    stdinRequestIdRef.current = null;
    setPendingInputRequestId(null);
    setStdinDraftState('');
  }, [pendingInputRequestId, ydoc]);

  const runAndSync = async (stdinVal, filesVal, runInteractiveStdin) => {
    const started = Date.now();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const appendStdoutChunk = (chunk) => {
      appendOutputText(chunk);
    };

    clearPendingInputState();
    isLocalRunRef.current = true;
    sceneStateRef.current = emptySceneState();
    keysByClientRef.current.clear();
    lastPeerSceneTsRef.current = 0;
    setLoading(true);
    setError('');
    setLastRunStartedAt(started);
    setLastRunFinishedAt(null);
    setLastRunDurationMs(null);
    setCodeAtLastRun(code ?? '');
    if (ydoc) {
      const m = ydoc.getMap(RUNNER_KEY);
      m.set('loading', true);
    }
    try {
      const result = await runPython(code || '', stdinVal, filesVal, {
        signal: controller.signal,
        interactiveStdin: runInteractiveStdin,
        echoInput: true,
        onStdoutChunk: appendStdoutChunk,
        onStdinRequest: runInteractiveStdin ? requestInteractiveStdin : undefined,
        onCanvas: (cmd) => {
          if (typeof window === 'undefined' || !cmd || typeof cmd !== 'object') return;
          // Тики/after — на control-шину (как в конспекте), иначе CodeCanvas их не запускает.
          if (cmd.type === '_control' || cmd.control) {
            const control = cmd.control || cmd.type;
            const type = (
              control === 'start_tick' || control === 'stop_tick'
              || control === 'after' || control === 'cancel_after'
            ) ? control : String(control);
            window.dispatchEvent(new CustomEvent('diobuch-canvas-control', {
              detail: { type, ms: cmd.ms, id: cmd.id },
            }));
            return;
          }
          window.dispatchEvent(new CustomEvent('diobuch-canvas', { detail: cmd }));
          trackSceneFromCmd(cmd);
          const t = String(cmd.type || '').toLowerCase();
          if (t === 'session') {
            flushScenePublish();
          } else if (
            t === 'scene_spawn' || t === 'scene_update' || t === 'scene_destroy'
            || t === 'scene_clear' || t === 'camera'
          ) {
            scheduleScenePublish();
          }
        },
        canvasConfig: canvasConfigRef.current,
        onStatus: (phase) => {
          if (phase === 'loading-python') {
            appendOutputText('Загрузка Python в браузере…\n');
          }
        },
      });
      const finished = Date.now();
      const durationMs = finished - started;
      setLastRunFinishedAt(finished);
      setLastRunDurationMs(durationMs);
      if (result.success) {
        const trimmed = trimOutputLines(result.output || '');
        cancelScheduledOutputSync();
        setOutput(trimmed);
        setError('');
        if (ydoc) {
          const m = ydoc.getMap(RUNNER_KEY);
          m.set('output', trimmed);
          m.set('error', '');
          m.set('loading', false);
          m.set('stdin', stdinVal || '');
          try { m.set('filesJson', JSON.stringify(filesVal || {})); } catch { /* ignore */ }
        }
      } else {
        const errMsg = result.error || 'Ошибка выполнения';
        cancelScheduledOutputSync();
        setError(errMsg);
        const trimmed = trimOutputLines(result.output || '');
        setOutput(trimmed);
        if (ydoc) {
          const m = ydoc.getMap(RUNNER_KEY);
          m.set('error', errMsg);
          m.set('output', trimmed);
          m.set('loading', false);
        }
      }
    } catch (e) {
      const finished = Date.now();
      setLastRunFinishedAt(finished);
      setLastRunDurationMs(finished - started);
      if (e?.name === 'AbortError') {
        setError('');
        const stoppedMsg = 'Остановлено';
        cancelScheduledOutputSync();
        setOutput((prev) => {
          const next = trimOutputLines(prev ? `${prev}\n\n${stoppedMsg}` : stoppedMsg);
          if (ydoc) {
            const m = ydoc.getMap(RUNNER_KEY);
            m.set('error', '');
            m.set('output', next);
            m.set('loading', false);
          }
          return next;
        });
      } else {
        cancelScheduledOutputSync();
        const errMsg = e.message || 'Ошибка выполнения';
        setError(errMsg);
        setOutput('');
        if (ydoc) {
          const m = ydoc.getMap(RUNNER_KEY);
          m.set('error', errMsg);
          m.set('output', '');
          m.set('loading', false);
        }
      }
    } finally {
      setLoading(false);
      setPeerLoading(false);
      abortControllerRef.current = null;
      isLocalRunRef.current = false;
      clearPendingInputState();
      clearAndPublishScene();
    }
  };

  const publishStopRequest = useCallback(() => {
    if (!ydoc) return null;
    const id = makeRunnerRequestId('stop');
    publishedStopRequestIdRef.current = id;
    lastStopRequestIdRef.current = id;
    try {
      ydoc.getMap(RUNNER_KEY).set('stopRequest', id);
    } catch {
      // ignore
    }
    return id;
  }, [ydoc]);

  const publishReconnectRequest = useCallback(() => {
    if (!ydoc) return null;
    const id = makeRunnerRequestId('reconnect');
    lastReconnectRequestIdRef.current = id;
    try {
      const m = ydoc.getMap(RUNNER_KEY);
      m.set('reconnectRequest', id);
    } catch {
      // ignore
    }
    return id;
  }, [ydoc]);

  const handleStop = useCallback((options) => {
    const skipPublish = options?.skipPublish === true;
    resetCanvasSessionUi();
    deliverCanvasEvent({ type: 'stop' });
    clearAndPublishScene();
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setLoading(false);
    setPeerLoading(false);
    cancelScheduledOutputSync();
    if (ydoc) {
      try {
        const m = ydoc.getMap(RUNNER_KEY);
        m.set('loading', false);
        if (!skipPublish) publishStopRequest();
      } catch {
        // ydoc уже уничтожен при уходе со страницы
      }
    }
  }, [ydoc, cancelScheduledOutputSync, publishStopRequest, clearAndPublishScene]);

  const requestPeerReconnect = useCallback(() => {
    handleStop();
    publishReconnectRequest();
  }, [handleStop, publishReconnectRequest]);

  const setPeerReconnectHandler = useCallback((fn) => {
    onPeerReconnectRef.current = typeof fn === 'function' ? fn : null;
  }, []);

  const handleRun = async () => {
    const effectiveFiles = {};
    Object.entries(files || {}).forEach(([name, content]) => {
      const key = sanitizeRunnerFileKey(name);
      if (!key || typeof content !== 'string' || !content.trim()) return;
      effectiveFiles[key] = content;
    });
    if (autoClearOutput) {
      clearOutput();
    } else if (output || error) {
      const nextOutput = output ? `${output}\n\n-----\n` : '';
      setOutput(nextOutput);
      setError('');
      if (ydoc) {
        const m = ydoc.getMap(RUNNER_KEY);
        m.set('output', nextOutput);
        m.set('error', '');
      }
    }
    if (ydoc) {
      const m = ydoc.getMap(RUNNER_KEY);
      m.set('loading', true);
      m.set('stdin', stdin || '');
      try { m.set('filesJson', JSON.stringify(files || {})); } catch { /* ignore */ }
    }
    await runAndSync(stdin, effectiveFiles, interactiveStdin);
  };

  const handleRunRef = useRef(handleRun);
  handleRunRef.current = handleRun;
  const handleStopRef = useRef(handleStop);
  handleStopRef.current = handleStop;

  useImperativeHandle(runRef, () => ({
    run: () => handleRunRef.current(),
    stop: () => handleStopRef.current(),
    toggle: () => {
      if (isLocalRunRef.current || abortControllerRef.current || loading || peerLoading) {
        handleStopRef.current();
      } else {
        void handleRunRef.current();
      }
    },
  }), [runRef, loading, peerLoading]);

  useEffect(() => () => {
    cancelScheduledOutputSync();
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (isLocalRunRef.current) {
      deliverCanvasEvent({ type: 'stop' });
      resetCanvasSessionUi();
      isLocalRunRef.current = false;
    }
    clearAndPublishScene();
  }, [ydoc, cancelScheduledOutputSync, clearAndPublishScene]);

  useEffect(() => {
    if (!ydoc) return;
    const m = ydoc.getMap(RUNNER_KEY);
    if (typeof m.get('interactiveStdin') !== 'boolean') {
      m.set('interactiveStdin', getStoredInteractiveStdin());
    }
  }, [ydoc]);

  useEffect(() => {
    if (!ydoc) return;
    runnerSignalsReadyRef.current = false;
    const m = ydoc.getMap(RUNNER_KEY);
    const apply = () => {
      const o = m.get('output');
      const e = m.get('error');
      const s = m.get('stdin');
      const filesJson = m.get('filesJson');
      const selStart = m.get('outputSelectionStart');
      const selEnd = m.get('outputSelectionEnd');
      const inputReq = m.get('inputRequestId');
      const inputRespId = m.get('inputResponseId');
      const inputRespLine = m.get('inputResponseLine');
      const inputDraft = m.get('inputDraft');
      const remoteInteractive = m.get('interactiveStdin');
      const remoteModalOpen = m.get('inputModalOpen');
      const stopReq = m.get('stopRequest');
      const reconnectReq = m.get('reconnectRequest');
      const remoteLoading = m.get('loading') === true;
      const canvasInputSeq = m.get('canvasInputSeq');
      const canvasInputEvent = m.get('canvasInputEvent');

      if (o !== undefined) setOutput(trimOutputLines(o));
      if (e !== undefined) setError(e);
      // Не очищаем loading у пира: Стоп учителя должен видеть «выполняется» и слать stopRequest.
      // Локальный run держит свой loading; чужой — peerLoading.
      if (!isLocalRunRef.current) {
        setPeerLoading(remoteLoading);
      } else {
        setPeerLoading(false);
      }
      if (s !== undefined) setStdin(s);
      if (typeof filesJson === 'string') {
        try {
          const parsed = JSON.parse(filesJson);
          if (parsed && typeof parsed === 'object') {
            setFiles(parsed);
            setAttachmentLoadErrors((prev) =>
              prev.filter((err) => {
                const v = parsed[err.name];
                return !(typeof v === 'string' && v.trim());
              }),
            );
          }
        } catch {
          // ignore
        }
      }
      if (typeof selStart === 'number' && typeof selEnd === 'number') {
        setOutputSelection({ start: Math.max(0, selStart), end: Math.max(0, selEnd) });
      }
      if (typeof inputDraft === 'string') {
        applyingRemoteDraftRef.current = true;
        setStdinDraftState(inputDraft);
        applyingRemoteDraftRef.current = false;
      }
      if (typeof remoteInteractive === 'boolean') {
        setInteractiveStdinState(remoteInteractive);
        try {
          localStorage.setItem(INTERACTIVE_STDIN_STORAGE_KEY, String(remoteInteractive));
        } catch {
          // ignore
        }
      }
      if (typeof remoteModalOpen === 'boolean') {
        setInputModalOpenState(remoteModalOpen);
      }
      const reqStr = inputReq != null && inputReq !== '' ? String(inputReq) : '';
      if (reqStr) {
        setPendingInputRequestId(reqStr);
      } else if (!isLocalRunRef.current) {
        setPendingInputRequestId(null);
      }
      if (
        isLocalRunRef.current &&
        inputRespId != null &&
        String(inputRespId) &&
        String(inputRespId) === String(stdinRequestIdRef.current || '')
      ) {
        deliverInteractiveStdinLine(typeof inputRespLine === 'string' ? inputRespLine : '');
        stdinRequestIdRef.current = null;
        setPendingInputRequestId(null);
        setStdinDraftState('');
        m.delete('inputResponseId');
        m.delete('inputResponseLine');
        m.delete('inputRequestId');
        m.set('inputDraft', '');
      }

      const stopId = stopReq != null && stopReq !== '' ? String(stopReq) : '';
      const reconnectId = reconnectReq != null && reconnectReq !== '' ? String(reconnectReq) : '';
      const canvasInId = canvasInputSeq != null && canvasInputSeq !== '' ? String(canvasInputSeq) : '';

      // Первый apply — только запомнить текущие id, не реагировать на старые сигналы в комнате.
      if (!runnerSignalsReadyRef.current) {
        if (stopId) lastStopRequestIdRef.current = stopId;
        if (reconnectId) lastReconnectRequestIdRef.current = reconnectId;
        if (canvasInId) lastCanvasInputSeqRef.current = canvasInId;
        runnerSignalsReadyRef.current = true;
        return;
      }

      if (
        canvasInId
        && canvasInId !== lastCanvasInputSeqRef.current
        && isLocalRunRef.current
        && canvasInputEvent
        && typeof canvasInputEvent === 'object'
      ) {
        lastCanvasInputSeqRef.current = canvasInId;
        const ev = canvasInputEvent;
        const t = String(ev.type || '');
        if (t === 'click') {
          deliverCanvasEvent({
            type: 'click',
            x: ev.x,
            y: ev.y,
          });
        } else if (t === 'keydown' || t === 'keyup') {
          applyMergedKeyEventRef.current(String(ev.clientId || 'peer'), {
            type: t,
            code: ev.code || '',
            key: ev.key || '',
          });
        }
      }

      if (
        stopId
        && stopId !== lastStopRequestIdRef.current
        && stopId !== publishedStopRequestIdRef.current
      ) {
        lastStopRequestIdRef.current = stopId;
        if (isLocalRunRef.current || abortControllerRef.current) {
          queueMicrotask(() => handleStopRef.current({ skipPublish: true }));
        } else if (remoteLoading) {
          try { m.set('loading', false); } catch { /* ignore */ }
          setPeerLoading(false);
        }
      }

      if (reconnectId && reconnectId !== lastReconnectRequestIdRef.current) {
        lastReconnectRequestIdRef.current = reconnectId;
        queueMicrotask(() => {
          if (typeof onPeerReconnectRef.current === 'function') {
            onPeerReconnectRef.current();
          }
        });
      }
    };
    apply();
    m.observe(apply);
    return () => {
      m.unobserve(apply);
      runnerSignalsReadyRef.current = false;
    };
  }, [ydoc]);

  const updateSelectionFromDom = useCallback(() => {
    const root = outputRef.current;
    if (!root) return;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) return;
    const offsets = rangeToOffsets(root, range);
    if (!offsets) return;
    const sel = {
      start: Math.max(0, offsets.start),
      end: Math.max(0, offsets.end),
    };
    setOutputSelection(sel);
    if (ydoc) {
      const m = ydoc.getMap(RUNNER_KEY);
      m.set('outputSelectionStart', sel.start);
      m.set('outputSelectionEnd', sel.end);
    }
  }, [ydoc]);

  const setStdinVal = (val) => {
    setStdin(val);
    if (ydoc) {
      const m = ydoc.getMap(RUNNER_KEY);
      m.set('stdin', val);
    }
  };
  const setFilesVal = useCallback((next) => {
    setFiles(next);
    if (ydoc) {
      const m = ydoc.getMap(RUNNER_KEY);
      try { m.set('filesJson', JSON.stringify(next || {})); } catch { /* ignore */ }
    }
  }, [ydoc]);

  const codeEditedSinceLastRun = Boolean(lastRunStartedAt && (code ?? '') !== (codeAtLastRun ?? ''));
  const showStop = loading || peerLoading;

  const ctx = {
    output,
    error,
    loading,
    peerLoading,
    running: showStop,
    showStop,
    lastRunStartedAt,
    lastRunFinishedAt,
    lastRunDurationMs,
    codeAtLastRun,
    codeEditedSinceLastRun,
    autoClearOutput,
    setAutoClearOutput,
    interactiveStdin,
    setInteractiveStdin,
    pendingInputRequestId,
    stdinDraft,
    setStdinDraft,
    submitInteractiveStdin,
    inputModalOpen,
    setInputModalOpen,
    clearOutput,
    stdin,
    setStdin: setStdinVal,
    files,
    setFiles: setFilesVal,
    attachmentLoadErrors,
    setAttachmentLoadErrors,
    handleRun,
    handleStop,
    requestPeerReconnect,
    setPeerReconnectHandler,
    outputRef,
    outputSelection,
    setOutputSelection,
    updateSelectionFromDom,
    trimOutputLines,
    fileInputRef,
    ydoc,
  };

  return <RunnerContext.Provider value={ctx}>{children}</RunnerContext.Provider>;
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ''));
    r.onerror = () => reject(r.error || new Error('read error'));
    r.readAsText(file);
  });
}

async function mergeFilesFromFileList(fileList, existingFiles, setFiles) {
  const list = Array.from(fileList || []).filter((f) => f && f.size !== undefined);
  if (!list.length) return;
  let next = { ...(existingFiles || {}) };
  for (const f of list) {
    const base = sanitizeRunnerFileKey(f.name) || 'file.txt';
    const name = uniqueRunnerFileName(base, Object.keys(next));
    const content = await readFileAsText(f);
    next = { ...next, [name]: content };
  }
  setFiles(next);
}

/** Список имён виртуальных файлов рядом с кнопкой Input в шапке редактора. */
export function EditorInputFilesBadge({ maxChars = 56 }) {
  const { files, attachmentLoadErrors } = useRunnerContext();
  const names = Object.keys(files || {}).sort((a, b) => a.localeCompare(b, 'ru-RU', { numeric: true }));
  const errs = Array.isArray(attachmentLoadErrors) ? attachmentLoadErrors : [];
  if (!names.length && !errs.length) return null;

  const okParts = names.map((n, i) => ({ kind: 'ok', id: `ok-${i}-${n}`, name: n, title: n }));
  const errParts = errs.map((e, i) => ({
    kind: 'err',
    id: `err-${i}-${e.name}`,
    name: e.name,
    title: e.message || `Не удалось загрузить «${e.name}»`,
  }));
  const full = [...names, ...errs.map((e) => e.name)].join(', ');
  const allParts = [...okParts, ...errParts];
  const joined = allParts.map((p) => p.name).join(', ');
  let truncated = false;
  let displayParts = allParts;
  if (joined.length > maxChars) {
    truncated = true;
    let acc = 0;
    displayParts = [];
    for (const p of allParts) {
      const sep = displayParts.length ? ', ' : '';
      if (acc + sep.length + p.name.length > maxChars - 1) break;
      displayParts.push(p);
      acc += sep.length + p.name.length;
    }
    if (displayParts.length === 0 && allParts.length > 0) {
      const p0 = allParts[0];
      displayParts = [
        {
          ...p0,
          name:
            p0.name.length > Math.max(1, maxChars - 1)
              ? `${p0.name.slice(0, Math.max(1, maxChars - 2))}…`
              : p0.name,
        },
      ];
    }
  }

  return (
    <span
      className="editor-input-files-badge"
      title={truncated ? full : undefined}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '0.2rem',
        fontSize: '0.78rem',
        maxWidth: '12rem',
        minWidth: 0,
      }}
    >
      {displayParts.map((p) =>
        p.kind === 'ok' ? (
          <span key={p.id} className="text-secondary" title={p.title}>
            {p.name}
          </span>
        ) : (
          <span
            key={p.id}
            className="text-danger"
            title={p.title}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}
          >
            <i className="fa-solid fa-triangle-exclamation ui-icon" style={{ fontSize: '0.7em' }} aria-hidden />
            {p.name}
          </span>
        ),
      )}
      {truncated && displayParts.length < allParts.length ? (
        <span className="text-secondary">…</span>
      ) : null}
    </span>
  );
}

/**
 * Подставляет вложения задачи в виртуальные файлы (полная замена набора).
 * @param {{ slug?: string | null, statement_attachments?: unknown } | null} task
 */
export function RunnerTaskAttachmentsSync({ task }) {
  const { setFiles, setAttachmentLoadErrors } = useRunnerContext();
  const lastKeyRef = useRef('');
  const genRef = useRef(0);

  useEffect(() => {
    const key = task?.slug || (task?.id != null ? `id:${task.id}` : '');
    if (!key) return;
    const raw = task.statement_attachments;
    const sig = `${key}\0${JSON.stringify(raw ?? null)}`;
    if (sig === lastKeyRef.current) return;
    lastKeyRef.current = sig;
    const gen = ++genRef.current;

    (async () => {
      try {
        const attachments = Array.isArray(raw) ? raw : [];
        if (attachments.length === 0) {
          if (gen !== genRef.current) return;
          setFiles({});
          setAttachmentLoadErrors([]);
          return;
        }
        const result = await fetchStatementAttachmentsAsFiles(attachments);
        if (gen !== genRef.current) return;
        setFiles(result.files);
        setAttachmentLoadErrors(result.loadErrors);
      } catch (e) {
        if (gen !== genRef.current) return;
        console.error('Не удалось подставить вложения задачи:', e);
        setAttachmentLoadErrors([
          { name: 'вложения', message: 'Не удалось загрузить файлы условия.' },
        ]);
      }
    })();
  }, [task?.slug, task?.id, task?.statement_attachments, setFiles, setAttachmentLoadErrors]);
  return null;
}

/**
 * То же для совместной сессии: вложения активной задачи из ydoc sessionMeta.
 */
export function RunnerSessionTaskAttachmentsSync({ ydoc }) {
  const { setFiles, setAttachmentLoadErrors } = useRunnerContext();
  const lastSigRef = useRef('');
  const genRef = useRef(0);

  useEffect(() => {
    lastSigRef.current = '';
    genRef.current = 0;
    if (!ydoc) return;
    const meta = ydoc.getMap('sessionMeta');
    const run = () => {
      const id = meta.get('activeTaskId');
      const rawJson = meta.get('activeTaskAttachmentsJson');
      if (id === undefined && rawJson === undefined) return;

      const sig = `${id ?? ''}|${typeof rawJson === 'string' ? rawJson : ''}`;
      if (sig === lastSigRef.current) return;
      lastSigRef.current = sig;
      const gen = ++genRef.current;

      let attachments = [];
      try {
        attachments = typeof rawJson === 'string' && rawJson ? JSON.parse(rawJson) : [];
        if (!Array.isArray(attachments)) attachments = [];
      } catch {
        attachments = [];
      }

      (async () => {
        try {
          if (attachments.length === 0) {
            if (gen !== genRef.current) return;
            setFiles({});
            setAttachmentLoadErrors([]);
            return;
          }
          const result = await fetchStatementAttachmentsAsFiles(attachments);
          if (gen !== genRef.current) return;
          setFiles(result.files);
          setAttachmentLoadErrors(result.loadErrors);
        } catch (e) {
          if (gen !== genRef.current) return;
          console.error('Не удалось подставить вложения активной задачи:', e);
          setAttachmentLoadErrors([
            { name: 'вложения', message: 'Не удалось загрузить файлы условия.' },
          ]);
        }
      })();
    };
    run();
    meta.observe(run);
    return () => meta.unobserve(run);
  }, [ydoc, setFiles, setAttachmentLoadErrors]);
  return null;
}

export function CollaborativeRunnerInputSection() {
  const {
    stdin,
    setStdin,
    files,
    setFiles,
    fileInputRef,
    interactiveStdin,
    setInteractiveStdin,
  } = useRunnerContext();

  const [dropActive, setDropActive] = useState(false);
  const existingFiles = Object.keys(files || {}).sort((a, b) => a.localeCompare(b, 'ru-RU', { numeric: true }));

  const addFile = () => {
    const keys = Object.keys(files || {});
    const name = uniqueRunnerFileName('input.txt', keys);
    setFiles({ ...(files || {}), [name]: '' });
  };

  const removeFile = (name) => {
    const next = { ...(files || {}) };
    delete next[name];
    setFiles(next);
  };

  const updateFile = (name, content) => {
    setFiles({ ...(files || {}), [name]: content });
  };

  const handleFilesSelect = async (e) => {
    const list = e.target.files;
    if (!list?.length) return;
    try {
      await mergeFilesFromFileList(list, files || {}, setFiles);
    } finally {
      e.target.value = '';
    }
  };

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types?.includes('Files')) {
      e.dataTransfer.dropEffect = 'copy';
      setDropActive(true);
    }
  }, []);

  const onDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) setDropActive(false);
  }, []);

  const onDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDropActive(false);
    const list = e.dataTransfer?.files;
    if (!list?.length) return;
    try {
      await mergeFilesFromFileList(list, files || {}, setFiles);
    } catch (err) {
      console.error(err);
    }
  }, [files, setFiles]);

  return (
    <div className="collab-input-section">
      <div className="input-group">
        <label className="stdin-mode-label">
          <input
            type="checkbox"
            checked={interactiveStdin}
            onChange={(e) => setInteractiveStdin(e.target.checked)}
          />
          <span>Интерактивный input() — пауза и ввод во время выполнения</span>
        </label>
        <p className="text-secondary stdin-mode-hint" style={{ fontSize: '0.85rem', margin: '0.35rem 0 0' }}>
          {interactiveStdin
            ? 'При input() программа ждёт строку в блоке «Вывод». Ввод и набор букв видят все участники; Enter может нажать любой.'
            : 'Строки ниже подставляются в input() заранее (режим автопроверки и тестов). В выводе будет строка input = …'}
        </p>
      </div>
      <div className="input-group">
        <label>Входные данные (stdin для input()):</label>
        <textarea
          value={stdin}
          onChange={(e) => setStdin(e.target.value)}
          placeholder={interactiveStdin
            ? 'Не используются в интерактивном режиме — вводите в блоке «Вывод» во время выполнения'
            : 'Введите данные построчно...'}
          rows={3}
          className="stdin-textarea"
          disabled={interactiveStdin}
        />
      </div>
      <div className="input-group">
        <label>Файлы (для чтения из файлов в программе):</label>
        <div
          className={`file-input-row file-input-dropzone${dropActive ? ' file-input-dropzone-active' : ''}`}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFilesSelect}
            style={{ display: 'none' }}
          />
          <button type="button" className="btn btn-outline btn-sm" onClick={() => fileInputRef.current?.click()}>
            📎 Прикрепить файлы
          </button>
          <button type="button" className="btn btn-outline btn-sm" onClick={addFile}>
            ＋ Файл вручную
          </button>
          <span className="text-secondary" style={{ fontSize: '0.85rem' }}>
            Имена в списке = имена в программе. Можно перетащить файлы сюда.
          </span>
        </div>
        {existingFiles.length === 0 ? (
          <p className="text-secondary" style={{ marginTop: '0.5rem' }}>Файлы не добавлены.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
            {existingFiles.map((name) => (
              <div key={name} style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.35rem' }}>
                  <strong style={{ fontFamily: 'var(--font-code)', fontSize: '0.9rem' }}>{name}</strong>
                  <button type="button" className="btn btn-outline btn-xs btn-icon-only" title="Удалить файл" aria-label="Удалить файл" onClick={() => removeFile(name)}>
                    ✕
                  </button>
                </div>
                <textarea
                  value={files?.[name] ?? ''}
                  onChange={(e) => updateFile(name, e.target.value)}
                  placeholder={`Содержимое ${name}...`}
                  rows={3}
                  className="file-textarea"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function formatRunTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatDuration(ms) {
  if (ms == null) return '';
  if (ms < 1000) return `${ms} мс`;
  return `${(ms / 1000).toFixed(1)} с`;
}

/**
 * Строка с временем последнего запуска, длительностью и кнопкой «Очистить вывод».
 * Рендерится в заголовке блока вывода (WorkPageBlocksLayout).
 */
export function OutputBlockRunInfo() {
  const {
    showStop,
    handleRun,
    handleStop,
    lastRunStartedAt,
    lastRunDurationMs,
    codeEditedSinceLastRun,
    clearOutput,
  } = useRunnerContext();

  const [elapsedMs, setElapsedMs] = useState(0);
  useEffect(() => {
    if (!showStop) return undefined;
    const started = lastRunStartedAt || Date.now();
    setElapsedMs(Date.now() - started);
    const t = setInterval(() => setElapsedMs(Date.now() - started), 250);
    return () => clearInterval(t);
  }, [showStop, lastRunStartedAt]);

  const timeStr = formatRunTime(lastRunStartedAt);
  const durationStr = showStop
    ? `(${formatDuration(elapsedMs || 0)})`
    : formatDuration(lastRunDurationMs);

  const showRunMeta = Boolean(lastRunStartedAt || showStop);

  return (
    <div className="output-block-run-info">
      {showStop ? (
        <button
          type="button"
          className="btn btn-small work-toolbar-btn work-toolbar-run output-run-btn running"
          onClick={handleStop}
          title="Остановить программу"
          aria-label="Остановить программу"
        >
          <i className="fa-solid fa-stop ui-icon" aria-hidden />
        </button>
      ) : (
        <button
          type="button"
          className="btn btn-small work-toolbar-btn work-toolbar-run output-run-btn"
          onClick={handleRun}
          title="Запустить программу (Ctrl+Enter)"
          aria-label="Запустить программу"
        >
          <i className="fa-solid fa-play ui-icon" aria-hidden />
        </button>
      )}
      {showRunMeta && (
        <>
          {codeEditedSinceLastRun && (
            <span
              className="output-code-edited-indicator"
              title="Код изменён после последнего запуска"
              aria-label="Код изменён после последнего запуска"
            >
              ·
            </span>
          )}
          {timeStr && <span className="output-run-time">{timeStr}</span>}
          {durationStr && <span className="output-run-duration">{durationStr}</span>}
          <button
            type="button"
            className="btn btn-outline btn-sm output-clear-btn"
            onClick={clearOutput}
            title="Очистить вывод"
            aria-label="Очистить вывод"
          >
            <i className="fa-solid fa-trash" aria-hidden />
          </button>
        </>
      )}
    </div>
  );
}

export function CollaborativeRunnerOutputSection() {
  const {
    output,
    error,
    outputRef,
    outputSelection,
    updateSelectionFromDom,
    pendingInputRequestId,
    stdinDraft,
    setStdinDraft,
    submitInteractiveStdin,
  } = useRunnerContext();

  const waitingForInput = Boolean(pendingInputRequestId);
  const stdinInputRef = useRef(null);
  const [overlayRects, setOverlayRects] = useState([]);

  useEffect(() => {
    if (!waitingForInput) return undefined;
    const t = window.setTimeout(() => stdinInputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [waitingForInput, pendingInputRequestId]);

  useLayoutEffect(() => {
    const root = outputRef.current;
    const { start, end } = outputSelection;
    if (!root || !output || start >= end) {
      setOverlayRects([]);
      return undefined;
    }
    const range = offsetsToRange(root, start, end);
    const next = range
      ? rangeRectsRelativeTo(root, range).map((rect, i) => ({ ...rect, key: `out-${i}` }))
      : [];
    setOverlayRects(next);
    return undefined;
  }, [output, outputRef, outputSelection]);

  const onInputKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitInteractiveStdin(stdinDraft);
    }
  };

  return (
    <div className="collab-output">
      {output && (
        <div
          className="output-block collab-output-scroll"
          onMouseUp={updateSelectionFromDom}
          onKeyUp={updateSelectionFromDom}
        >
          <div className="collab-output-highlight-root" ref={outputRef}>
            <pre className="collab-output-text">{output}</pre>
            <div className="output-selection-layer" aria-hidden="true">
              {overlayRects.map((r) => (
                <span
                  key={r.key}
                  className="output-selection"
                  style={{ top: r.top, left: r.left, width: r.width, height: r.height }}
                />
              ))}
            </div>
          </div>
        </div>
      )}
      {waitingForInput && (
        <div className="runner-interactive-stdin">
          <span className="runner-interactive-stdin-label">input()</span>
          <input
            ref={stdinInputRef}
            type="text"
            className="form-input runner-interactive-stdin-input"
            value={stdinDraft}
            onChange={(e) => setStdinDraft(e.target.value)}
            onKeyDown={onInputKeyDown}
            onClick={(e) => e.stopPropagation()}
            placeholder="Введите строку и нажмите Enter"
            autoFocus
            aria-label="Интерактивный ввод для input()"
          />
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => submitInteractiveStdin(stdinDraft)}
          >
            Enter
          </button>
        </div>
      )}
      {error && (
        <pre className="output-block text-error">{error}</pre>
      )}
    </div>
  );
}

/** Общая модалка Input: открытие/закрытие синкается через Yjs. */
export function CollaborativeRunnerInputModal({ children }) {
  const { inputModalOpen, setInputModalOpen } = useRunnerContext();
  return (
    <Modal open={inputModalOpen} title="Input" onClose={() => setInputModalOpen(false)}>
      {children}
    </Modal>
  );
}
